// Recording a payment on a bill that already exists — SEPARATE FROM APPROVING IT (docs/payment-status-gap.md).
// Approval covers what was billed (values, rate, lines, installment amounts, due dates). Marking paid, editing a payment
// or marking unpaid writes ONLY the payment fields (paid_source / paid_at / paid_amount / paid_proof / paid_note /
// paid_recorded_by / paid_recorded_at) and never sends the bill back to Approval. Used by the parcel card's Taxes tab
// ("Mark paid", "Mark all installments paid") and the scanner's "$0.00 prior" prompt. Unit-tested:
// src/__tests__/payment-record.test.js.
//
// ⛔ ONE STATUS MOVE, BOTH WAYS (Philip, 2026-10-13). Approving a paid bill FILES it (TaxBillApprovals.approveIds),
// so an APPROVED bill marked paid moves to FILED the same way — a paid bill is never transmitted. Transmitted and
// filed bills keep their status. Marking unpaid a bill that was filed THIS way returns it to APPROVED; the move is
// recognised from its own activity_log 'mark_paid' entry (details.status_from 'approved'), so a bill filed by the
// CS (non-transmit client) or by approving a paid bill stays filed.
// ⛔ transmittal_id IS CLEARED on approved → filed. A generated-but-unsent TBT still points at the bill, and marking
// that TBT sent sets status 'transmitted' on every bill carrying its id (TaxBillTransmittals) — it would pull a
// filed paid bill back to transmitted. Reopen clears it for the same reason.
// ⛔ Every write checks its affected-row count: tax_bills is RLS-enabled and a blocked update returns 0 rows, no error.
import { supabase } from '../supabase';
import { TAX_BILL_STATUS } from '../constants/statuses';

export const PAID_PROOFS = [
  { key: 'receipt', label: 'Receipt' },
  { key: 'later_bill_zero_prior', label: 'Later bill shows $0 prior' },
  { key: 'collector_website', label: 'Collector website' },
  { key: 'client_confirmation', label: 'Client confirmation' },
  { key: 'other', label: 'Other' },
];
export const proofLabel = (key) => (PAID_PROOFS.find(p => p.key === key) || {}).label || null;

// What the installment billed: the full tax when stored, else (older bills) its amount due.
export const billedOf = (b) => (b?.billed_amount != null ? Number(b.billed_amount) : (b?.total_bill_amount != null ? Number(b.total_bill_amount) : null));

const BILL_COLS = 'id,status,superseded_at,transmittal_id,billed_amount,total_bill_amount,paid_source,paid_at,paid_amount,paid_proof,paid_note';
const money = (v) => {
  if (v == null || String(v).trim() === '') return null;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
};
const payOf = (b) => ({ source: b.paid_source || null, amount: b.paid_amount ?? null, at: b.paid_at || null, proof: b.paid_proof || null, note: b.paid_note || null });

// Validates the dialog's fields. → { ok:true, amount, date, proof, note } | { ok:false, reason }
export function checkPayment({ amount, date, proof, note }) {
  const amt = money(amount);
  if (Number.isNaN(amt) || (amt != null && amt < 0)) return { ok: false, reason: 'Paid amount must be a number (or blank).' };
  const d = String(date || '').trim();
  if (d && !/^\d{4}-\d{2}-\d{2}$/.test(d)) return { ok: false, reason: 'Paid date must be a date (or blank).' };
  if (!PAID_PROOFS.some(p => p.key === proof)) return { ok: false, reason: 'Pick the proof of payment.' };
  const n = String(note || '').trim();
  if (n.length > 300) return { ok: false, reason: 'The note is limited to 300 characters.' };
  return { ok: true, amount: amt, date: d || null, proof, note: n || null };
}

async function readBill(id) {
  const { data, error } = await supabase.from('tax_bills').select(BILL_COLS).eq('id', id).maybeSingle();
  if (error) throw new Error(`Could not read the bill: ${error.message}`);
  if (!data) throw new Error('The bill was not found.');
  if (data.superseded_at) throw new Error('This bill has been replaced by a newer one — refresh.');
  return data;
}

// Writes `patch` only if the bill's status is still `status` (nobody moved it meanwhile). Throws on 0 rows.
async function writeBill(id, status, patch) {
  const { data, error } = await supabase.from('tax_bills').update(patch).eq('id', id).eq('status', status).select('id');
  if (error) throw new Error(error.message);
  if (!data || data.length !== 1) throw new Error('No bill was updated — it changed meanwhile, or permission was refused. Refresh and retry.');
}

async function logIt(action, billId, entityName, staffName, details) {
  const { error } = await supabase.from('activity_log').insert({
    staff_user: staffName || 'NTRG Staff', action, entity_type: 'tax_bill', entity_id: billId, entity_name: entityName || billId, details,
  });
  if (error) console.warn(`[paymentRecord] activity_log ${action}:`, error.message);
}

// Mark paid, or edit the payment of a bill already paid. Fields as typed in the dialog (checkPayment).
// → { statusFrom, statusTo }. Throws with a message a person can read.
export async function recordPayment({ billId, amount, date, proof, note, staffName, entityName }) {
  const v = checkPayment({ amount, date, proof, note });
  if (!v.ok) throw new Error(v.reason);
  const b = await readBill(billId);
  if (b.status === TAX_BILL_STATUS.REJECTED) throw new Error('This bill was returned to processing — record the payment when it is processed again.');
  const editing = !!b.paid_source;
  const statusTo = b.status === TAX_BILL_STATUS.APPROVED ? TAX_BILL_STATUS.FILED : b.status;
  const patch = {
    paid_source: 'staff', paid_amount: v.amount, paid_at: v.date, paid_proof: v.proof, paid_note: v.note,
    paid_recorded_by: staffName || 'NTRG Staff', paid_recorded_at: new Date().toISOString(),
    ...(statusTo !== b.status ? { status: statusTo, transmittal_id: null } : {}),
  };
  await writeBill(billId, b.status, patch);
  await logIt(editing ? 'edit_payment' : 'mark_paid', billId, entityName, staffName, {
    before: payOf(b), after: { source: 'staff', amount: v.amount, at: v.date, proof: v.proof, note: v.note },
    billed: billedOf(b), status_from: b.status, status_to: statusTo,
    ...(b.transmittal_id && statusTo !== b.status ? { cleared_transmittal_id: b.transmittal_id } : {}),
  });
  return { statusFrom: b.status, statusTo };
}

// Was this FILED bill filed by marking it paid while Approved? Its latest 'mark_paid' entry says so.
async function filedByMarkPaid(billId) {
  const { data, error } = await supabase.from('activity_log').select('details,created_at')
    .eq('entity_type', 'tax_bill').eq('entity_id', billId).eq('action', 'mark_paid')
    .order('created_at', { ascending: false }).limit(1);
  if (error) throw new Error(`Could not read the bill's history: ${error.message}`);
  const d = (data && data[0] && data[0].details) || {};
  return d.status_from === TAX_BILL_STATUS.APPROVED && d.status_to === TAX_BILL_STATUS.FILED;
}

// Mark unpaid (a payment recorded by mistake). → { statusFrom, statusTo }.
export async function clearPayment({ billId, staffName, entityName, reason }) {
  const b = await readBill(billId);
  if (!b.paid_source) throw new Error('This bill is not marked paid.');
  const back = b.status === TAX_BILL_STATUS.FILED && await filedByMarkPaid(billId);
  const statusTo = back ? TAX_BILL_STATUS.APPROVED : b.status;
  await writeBill(billId, b.status, {
    paid_source: null, paid_amount: null, paid_at: null, paid_proof: null, paid_note: null,
    paid_recorded_by: staffName || 'NTRG Staff', paid_recorded_at: new Date().toISOString(),
    ...(back ? { status: statusTo } : {}),
  });
  await logIt('mark_unpaid', billId, entityName, staffName, {
    before: payOf(b), reason: String(reason || '').trim() || null, status_from: b.status, status_to: statusTo,
  });
  return { statusFrom: b.status, statusTo };
}

// ═══ "PRIOR YEAR(S) $0.00" — a later bill that prints nothing owed for prior years proves the earlier ones were paid.
// From one parcel-collector's live bills, the earlier installments still not marked paid, split into the ones that can
// be marked and the Returned ones (re-processed, not marked). "Earlier" = due before this bill's first due date; a
// bill with no due date on either side compares by tax year instead. → { markable: [...], returned: [...] }
export function priorUnpaid(bills, { billId, dueDate, taxYear }) {
  const ty = Number(taxYear);
  const earlier = (b) => (dueDate && b.due_date)
    ? String(b.due_date) < String(dueDate)
    : (Number.isFinite(ty) && Number(b.tax_year) < ty);
  const open = (bills || [])
    .filter(b => b && b.id !== billId && !b.superseded_at && !b.paid_source && earlier(b))
    .sort((a, b) => String(a.tax_year).localeCompare(String(b.tax_year))
      || (a.installment_number ?? 99) - (b.installment_number ?? 99) || String(a.due_date || '').localeCompare(String(b.due_date || '')));
  return {
    markable: open.filter(b => b.status !== TAX_BILL_STATUS.REJECTED),
    returned: open.filter(b => b.status === TAX_BILL_STATUS.REJECTED),
  };
}

// The proof a "$0.00 prior" mark carries: Later bill shows $0 prior, noted with the later bill's tax year.
export const zeroPriorProof = (taxYear) => ({ proof: 'later_bill_zero_prior', note: `${taxYear} bill` });
